import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import PageShell from "@/components/PageShell";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Check, X, Users, Briefcase, FileText, FlaskConical, Archive, Globe, UserPlus, Search, GraduationCap, Shield } from "lucide-react";

const TABS = ["Overview", "Jobs", "Applications", "Projects", "Candidates", "Team"];

export default function Admin() {
  const [tab, setTab] = useState("Overview");
  return (
    <PageShell>
      <div>
        <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#7A222B]">Admin</span>
        <h1 className="font-display mt-2 text-3xl sm:text-4xl font-bold tracking-tight text-[#2D2825]" style={{ fontWeight: 700 }}>Moderation dashboard</h1>
      </div>
      <div className="mt-8 flex gap-2 flex-wrap border-b border-[#EAE3DB] pb-4">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t.toLowerCase()}`}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${tab === t ? "bg-[#7A222B] text-white" : "bg-white border border-[#EAE3DB] text-[#6E665E] hover:border-[#D99B9F]"}`}>
            {t}
          </button>
        ))}
      </div>
      <div className="mt-8">
        {tab === "Overview" && <Overview />}
        {tab === "Jobs" && <JobsAdmin />}
        {tab === "Applications" && <AppsAdmin />}
        {tab === "Projects" && <ProjectsAdmin />}
        {tab === "Candidates" && <CandidatesAdmin />}
        {tab === "Team" && <TeamAdmin />}
      </div>
    </PageShell>
  );
}

function Overview() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);
  if (!stats) return <Loader2 className="w-8 h-8 animate-spin text-[#7A222B] mx-auto my-20" />;
  const cards = [
    { label: "Candidates", value: stats.students, icon: Users },
    { label: "Admins", value: stats.admins, icon: Shield },
    { label: "Jobs live", value: stats.jobs_published, icon: Globe },
    { label: "Jobs draft", value: stats.jobs_draft, icon: FileText },
    { label: "Applications", value: stats.applications, icon: Briefcase },
    { label: "Projects live", value: stats.projects_published, icon: FlaskConical },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-3 gap-6">
      {cards.map((c) => (
        <div key={c.label} data-testid={`stat-${c.label.toLowerCase().replace(/[^a-z]/g, "-")}`} className="bg-white border border-[#EAE3DB] rounded-3xl p-7">
          <div className="w-11 h-11 rounded-2xl bg-[#FDF0EC] flex items-center justify-center"><c.icon className="w-5 h-5 text-[#7A222B]" /></div>
          <p className="mt-4 font-display text-4xl font-bold text-[#2D2825]" style={{ fontWeight: 800 }}>{c.value}</p>
          <p className="mt-1 text-sm text-[#6E665E]">{c.label}</p>
        </div>
      ))}
    </div>
  );
}

const inputCls = "w-full rounded-xl border border-[#EAE3DB] bg-white px-4 py-2.5 text-sm text-[#2D2825] focus:outline-none focus:border-[#7A222B] transition";

function JobsAdmin() {
  const [jobs, setJobs] = useState(null);
  const [show, setShow] = useState(false);
  const blank = { title: "", company: "", category: "AI Analyst", job_type: "Internship", location: "", work_mode: "Remote", stipend: "", description: "", skills: "" };
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/jobs").then((r) => setJobs(r.data)).catch(() => setJobs([]));
  useEffect(() => { load(); }, []);

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/admin/jobs", { ...form, skills: form.skills.split(",").map((s) => s.trim()).filter(Boolean) });
      toast.success("Job created"); setShow(false); setForm(blank); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const setStatus = async (id, status) => { await api.patch(`/admin/jobs/${id}`, { status }); toast.success(status === "published" ? "Published — now live" : `Moved to ${status}`); load(); };
  const del = async (id) => { await api.delete(`/admin/jobs/${id}`); toast.success("Deleted"); load(); };

  if (jobs === null) return <Loader2 className="w-8 h-8 animate-spin text-[#7A222B] mx-auto my-20" />;
  return (
    <div>
      <button onClick={() => setShow(!show)} data-testid="admin-add-job-btn" className="inline-flex items-center gap-2 bg-[#7A222B] hover:bg-[#5C1D24] text-white px-5 py-2.5 rounded-full text-sm font-medium transition-colors">
        <Plus className="w-4 h-4" /> Add job
      </button>
      {show && (
        <div className="mt-5 bg-white border border-[#EAE3DB] rounded-3xl p-6 grid sm:grid-cols-2 gap-4" data-testid="admin-job-form">
          <input className={inputCls} placeholder="Title" data-testid="job-form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <input className={inputCls} placeholder="Company" data-testid="job-form-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
          <select className={inputCls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
            {["AI Analyst", "Prompt Engineer", "AI Operations", "Data Roles", "ML Internship", "Automation"].map((c) => <option key={c}>{c}</option>)}
          </select>
          <select className={inputCls} value={form.job_type} onChange={(e) => setForm({ ...form, job_type: e.target.value })}>
            {["Internship", "Freelance", "Entry-level"].map((c) => <option key={c}>{c}</option>)}
          </select>
          <input className={inputCls} placeholder="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
          <select className={inputCls} value={form.work_mode} onChange={(e) => setForm({ ...form, work_mode: e.target.value })}>
            {["Remote", "On-site", "Hybrid"].map((c) => <option key={c}>{c}</option>)}
          </select>
          <input className={inputCls} placeholder="Stipend (e.g. ₹25,000/mo)" value={form.stipend} onChange={(e) => setForm({ ...form, stipend: e.target.value })} />
          <input className={inputCls} placeholder="Skills (comma separated)" value={form.skills} onChange={(e) => setForm({ ...form, skills: e.target.value })} />
          <textarea className={`${inputCls} sm:col-span-2`} rows={3} placeholder="Description" data-testid="job-form-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <button onClick={create} disabled={saving} data-testid="job-form-submit" className="sm:col-span-2 flex items-center justify-center gap-2 bg-[#7A222B] text-white py-2.5 rounded-xl font-medium disabled:opacity-60">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save as draft
          </button>
        </div>
      )}
      <div className="mt-6 space-y-3">
        {jobs.map((j) => (
          <div key={j.id} data-testid={`admin-job-${j.id}`} className="bg-white border border-[#EAE3DB] rounded-2xl px-6 py-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-semibold text-[#2D2825]">{j.title} <span className="text-sm text-[#6E665E] font-normal">· {j.company}</span></p>
              <p className="text-xs text-[#948A80]">{j.job_type} · {j.location} · {j.work_mode}</p>
            </div>
            <div className="flex items-center gap-2">
              <StatusTag status={j.status} />
              {j.status !== "published" && <button onClick={() => setStatus(j.id, "published")} data-testid={`job-publish-${j.id}`} title="Publish (make live)" className="p-2 rounded-lg bg-[#E6F4EA] text-[#2E7D32] hover:opacity-80 transition-opacity"><Globe className="w-4 h-4" /></button>}
              {j.status === "published" && <button onClick={() => setStatus(j.id, "draft")} data-testid={`job-unpublish-${j.id}`} title="Unpublish (back to draft)" className="p-2 rounded-lg bg-[#FEF3E0] text-[#C67D0A] hover:opacity-80 transition-opacity"><FileText className="w-4 h-4" /></button>}
              {j.status !== "archived" && <button onClick={() => setStatus(j.id, "archived")} data-testid={`job-archive-${j.id}`} title="Archive" className="p-2 rounded-lg bg-[#FAF0F1] text-[#7A222B] hover:opacity-80 transition-opacity"><Archive className="w-4 h-4" /></button>}
              <button onClick={() => del(j.id)} data-testid={`job-delete-${j.id}`} className="p-2 rounded-lg bg-[#F5EFE6] text-[#6E665E] hover:text-[#7A222B] transition-colors"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AppsAdmin() {
  const [apps, setApps] = useState(null);
  const load = () => api.get("/admin/applications").then((r) => setApps(r.data)).catch(() => setApps([]));
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => { await api.patch(`/admin/applications/${id}`, { status }); toast.success(`Marked ${status}`); load(); };
  if (apps === null) return <Loader2 className="w-8 h-8 animate-spin text-[#7A222B] mx-auto my-20" />;
  if (apps.length === 0) return <div className="bg-white border border-[#EAE3DB] rounded-2xl p-12 text-center text-[#6E665E]">No applications yet.</div>;
  return (
    <div className="space-y-3">
      {apps.map((a) => (
        <div key={a.id} data-testid={`admin-app-${a.id}`} className="bg-white border border-[#EAE3DB] rounded-2xl px-6 py-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-semibold text-[#2D2825]">{a.applicant_name} <span className="font-normal text-sm text-[#6E665E]">→ {a.job_title} @ {a.company}</span></p>
              <p className="text-xs text-[#948A80]">{a.applicant_email}{a.resume_link ? ` · resume: ${a.resume_link}` : ""}</p>
            </div>
            <div className="flex items-center gap-2">
              <StatusTag status={a.status} />
              <button onClick={() => setStatus(a.id, "shortlisted")} data-testid={`app-shortlist-${a.id}`} className="p-2 rounded-lg bg-[#E6F4EA] text-[#2E7D32] hover:opacity-80 transition-opacity"><Check className="w-4 h-4" /></button>
              <button onClick={() => setStatus(a.id, "rejected")} data-testid={`app-reject-${a.id}`} className="p-2 rounded-lg bg-[#FAF0F1] text-[#7A222B] hover:opacity-80 transition-opacity"><X className="w-4 h-4" /></button>
            </div>
          </div>
          {a.cover_note && <p className="mt-3 text-sm text-[#6E665E] bg-[#F5EFE6] rounded-xl px-4 py-2">{a.cover_note}</p>}
        </div>
      ))}
    </div>
  );
}

function ProjectsAdmin() {
  const [items, setItems] = useState(null);
  const blank = { title: "", category: "Prompting", difficulty: "Beginner", duration: "", brief: "", deliverables: "", skills: "" };
  const [form, setForm] = useState(blank);
  const [show, setShow] = useState(false);
  const load = () => api.get("/admin/projects").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const create = async () => {
    try { await api.post("/admin/projects", { ...form, deliverables: form.deliverables.split(",").map((s) => s.trim()).filter(Boolean), skills: form.skills.split(",").map((s) => s.trim()).filter(Boolean) }); toast.success("Saved as draft"); setShow(false); setForm(blank); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const setStatus = async (id, status) => { await api.patch(`/admin/projects/${id}`, { status }); toast.success(status === "published" ? "Published — now live" : `Moved to ${status}`); load(); };
  const del = async (id) => { await api.delete(`/admin/projects/${id}`); toast.success("Deleted"); load(); };
  if (items === null) return <Loader2 className="w-8 h-8 animate-spin text-[#7A222B] mx-auto my-20" />;
  return (
    <div>
      <button onClick={() => setShow(!show)} data-testid="admin-add-project-btn" className="inline-flex items-center gap-2 bg-[#7A222B] text-white px-5 py-2.5 rounded-full text-sm font-medium"><Plus className="w-4 h-4" /> Add project</button>
      {show && (
        <div className="mt-5 bg-white border border-[#EAE3DB] rounded-3xl p-6 grid sm:grid-cols-2 gap-4">
          <input className={inputCls} placeholder="Title" data-testid="project-form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <select className={inputCls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
            {["Prompting", "Automation", "No-code AI", "Data Basics", "Model Evaluation", "AI Operations"].map((c) => <option key={c}>{c}</option>)}
          </select>
          <select className={inputCls} value={form.difficulty} onChange={(e) => setForm({ ...form, difficulty: e.target.value })}>
            {["Beginner", "Intermediate", "Advanced"].map((c) => <option key={c}>{c}</option>)}
          </select>
          <input className={inputCls} placeholder="Duration (e.g. 1 week)" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} />
          <textarea className={`${inputCls} sm:col-span-2`} rows={2} placeholder="Brief" value={form.brief} onChange={(e) => setForm({ ...form, brief: e.target.value })} />
          <input className={inputCls} placeholder="Deliverables (comma separated)" value={form.deliverables} onChange={(e) => setForm({ ...form, deliverables: e.target.value })} />
          <input className={inputCls} placeholder="Skills (comma separated)" value={form.skills} onChange={(e) => setForm({ ...form, skills: e.target.value })} />
          <button onClick={create} data-testid="project-form-submit" className="sm:col-span-2 bg-[#7A222B] text-white py-2.5 rounded-xl font-medium">Save as draft</button>
        </div>
      )}
      <div className="mt-6 space-y-3">
        {items.map((i) => (
          <div key={i.id} data-testid={`admin-project-${i.id}`} className="bg-white border border-[#EAE3DB] rounded-2xl px-6 py-4 flex flex-wrap items-center justify-between gap-3">
            <div><p className="font-semibold text-[#2D2825]">{i.title}</p><p className="text-xs text-[#948A80]">{i.category} · {i.difficulty} · {i.duration}</p></div>
            <div className="flex items-center gap-2">
              <StatusTag status={i.status} />
              {i.status !== "published" && <button onClick={() => setStatus(i.id, "published")} data-testid={`project-publish-${i.id}`} title="Publish (make live)" className="p-2 rounded-lg bg-[#E6F4EA] text-[#2E7D32] hover:opacity-80 transition-opacity"><Globe className="w-4 h-4" /></button>}
              {i.status === "published" && <button onClick={() => setStatus(i.id, "draft")} data-testid={`project-unpublish-${i.id}`} title="Unpublish" className="p-2 rounded-lg bg-[#FEF3E0] text-[#C67D0A] hover:opacity-80 transition-opacity"><FileText className="w-4 h-4" /></button>}
              <button onClick={() => del(i.id)} data-testid={`project-delete-${i.id}`} className="p-2 rounded-lg bg-[#F5EFE6] text-[#6E665E] hover:text-[#7A222B] transition-colors"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CandidatesAdmin() {
  const [list, setList] = useState(null);
  const [q, setQ] = useState("");
  const load = (query = "") => api.get(`/admin/candidates${query ? `?q=${encodeURIComponent(query)}` : ""}`).then((r) => setList(r.data)).catch(() => setList([]));
  useEffect(() => { load(); }, []);
  if (list === null) return <Loader2 className="w-8 h-8 animate-spin text-[#7A222B] mx-auto my-20" />;
  return (
    <div>
      <div className="relative max-w-md">
        <Search className="w-4 h-4 text-[#948A80] absolute left-3.5 top-1/2 -translate-y-1/2" />
        <input value={q} onChange={(e) => { setQ(e.target.value); load(e.target.value); }} data-testid="candidate-search"
          placeholder="Search by name, email, college or skill…"
          className="pl-10 pr-4 py-2.5 rounded-full border border-[#EAE3DB] bg-white text-sm w-full focus:outline-none focus:border-[#7A222B] transition" />
      </div>
      {list.length === 0 ? (
        <div className="mt-6 bg-white border border-[#EAE3DB] rounded-2xl p-12 text-center text-[#6E665E]" data-testid="no-candidates">No candidates found.</div>
      ) : (
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
          {list.map((c) => (
            <div key={c.id} data-testid={`candidate-${c.id}`} className="bg-white border border-[#EAE3DB] rounded-2xl p-6">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-semibold text-[#2D2825]">{c.name || "Unnamed"}</p>
                  <p className="text-xs text-[#948A80]">{c.email}</p>
                  {c.headline && <p className="mt-1 text-sm text-[#6E665E]">{c.headline}</p>}
                </div>
                {c.profile_complete
                  ? <span className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1 rounded-full bg-[#E6F4EA] text-[#2E7D32]"><Check className="w-3 h-3" /> Complete</span>
                  : <span className="text-xs font-semibold px-3 py-1 rounded-full bg-[#FEF3E0] text-[#C67D0A]">Incomplete</span>}
              </div>
              {(c.college || c.location || c.graduation_year) && (
                <p className="mt-3 text-xs text-[#948A80] flex items-center gap-2"><GraduationCap className="w-3.5 h-3.5" /> {[c.college, c.graduation_year, c.location].filter(Boolean).join(" · ")}</p>
              )}
              {c.skills?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {c.skills.slice(0, 6).map((s) => <span key={s} className="text-xs bg-[#FDF0EC] text-[#7A222B] px-2.5 py-1 rounded-full">{s}</span>)}
                </div>
              )}
              {(c.resume_link || c.github || c.linkedin) && (
                <div className="mt-4 flex flex-wrap gap-3 text-xs">
                  {c.resume_link && <a href={c.resume_link} target="_blank" rel="noreferrer" className="text-[#7A222B] hover:underline">Resume ↗</a>}
                  {c.github && <a href={c.github.startsWith("http") ? c.github : `https://${c.github}`} target="_blank" rel="noreferrer" className="text-[#7A222B] hover:underline">GitHub ↗</a>}
                  {c.linkedin && <a href={c.linkedin.startsWith("http") ? c.linkedin : `https://${c.linkedin}`} target="_blank" rel="noreferrer" className="text-[#7A222B] hover:underline">LinkedIn ↗</a>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TeamAdmin() {
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/admin/team").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const invite = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data: res } = await api.post("/admin/team/invite", { email });
      toast.success(res.message); setEmail(""); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setSaving(false); }
  };
  const revoke = async (em) => {
    try { await api.delete(`/admin/team/${encodeURIComponent(em)}`); toast.success("Admin access revoked"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  if (!data) return <Loader2 className="w-8 h-8 animate-spin text-[#7A222B] mx-auto my-20" />;
  return (
    <div className="max-w-2xl">
      <form onSubmit={invite} className="bg-white border border-[#EAE3DB] rounded-3xl p-6">
        <h3 className="font-semibold text-[#2D2825]">Invite a teammate as admin</h3>
        <p className="text-sm text-[#6E665E] mt-1">They can publish jobs, projects and review candidates. If they already have an account they're promoted instantly; otherwise they become admin on their next sign in.</p>
        <div className="mt-4 flex gap-2">
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="team-invite-email"
            placeholder="teammate@company.com" className={`${inputCls} flex-1`} />
          <button type="submit" disabled={saving} data-testid="team-invite-btn" className="inline-flex items-center gap-2 bg-[#7A222B] hover:bg-[#5C1D24] text-white px-5 rounded-xl font-medium transition-colors disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Invite
          </button>
        </div>
      </form>

      <h3 className="mt-8 font-semibold text-[#2D2825]">Current admins</h3>
      <div className="mt-3 space-y-2">
        {data.admins.map((a) => (
          <div key={a.email} data-testid={`team-admin-${a.email}`} className="bg-white border border-[#EAE3DB] rounded-2xl px-5 py-3.5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-[#2D2825] flex items-center justify-center"><Shield className="w-4 h-4 text-[#F8D7C4]" /></div>
              <div>
                <p className="font-medium text-[#2D2825]">{a.name || a.email}</p>
                <p className="text-xs text-[#948A80]">{a.email}</p>
              </div>
            </div>
            {a.is_owner
              ? <span className="text-xs font-semibold px-3 py-1 rounded-full bg-[#F5EFE6] text-[#6E665E]">Owner</span>
              : <button onClick={() => revoke(a.email)} data-testid={`team-revoke-${a.email}`} className="text-xs font-semibold text-[#7A222B] hover:underline">Revoke</button>}
          </div>
        ))}
      </div>

      {data.pending_invites.length > 0 && (
        <>
          <h3 className="mt-8 font-semibold text-[#2D2825]">Pending invites</h3>
          <div className="mt-3 space-y-2">
            {data.pending_invites.map((i) => (
              <div key={i.email} data-testid={`team-invite-${i.email}`} className="bg-[#FDF0EC] border border-[#F8D7C4] rounded-2xl px-5 py-3.5 flex items-center justify-between">
                <div>
                  <p className="font-medium text-[#2D2825]">{i.email}</p>
                  <p className="text-xs text-[#948A80]">Invited · becomes admin on sign in</p>
                </div>
                <button onClick={() => revoke(i.email)} data-testid={`team-cancel-${i.email}`} className="text-xs font-semibold text-[#7A222B] hover:underline">Cancel</button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function StatusTag({ status }) {
  const map = {
    published: "bg-[#E6F4EA] text-[#2E7D32]", draft: "bg-[#FEF3E0] text-[#C67D0A]", archived: "bg-[#F5EFE6] text-[#948A80]",
    submitted: "bg-[#F5EFE6] text-[#6E665E]", shortlisted: "bg-[#E6F4EA] text-[#2E7D32]", rejected: "bg-[#FAF0F1] text-[#7A222B]",
  };
  return <span className={`text-xs font-semibold px-3 py-1 rounded-full capitalize ${map[status] || "bg-[#F5EFE6] text-[#6E665E]"}`}>{status}</span>;
}
